import { useDispatch, useSelector } from 'react-redux'
import {
  fetchData,
  getData,
  localRequest,
  postData,
} from 'src/redux/slices/api'

// ----------------------------------------------------------------------

const SCOUT_MOCK_MODE = String(process.env.REACT_APP_SCOUT_MOCK_MODE || '').toLowerCase() === 'true'
const SCOUT_MOCK_BASE = String(process.env.REACT_APP_SCOUT_MOCK_BASE || '/scout-mocks').replace(/\/+$/, '')
const API_DEBUG_LOGS = String(process.env.REACT_APP_API_DEBUG_LOGS || '').toLowerCase() === 'true'
const apiDebug = (...args) => {
  if (API_DEBUG_LOGS) console.log(...args)
}
const safeCallback = (callback, payload) => {
  if (typeof callback === 'function') callback(payload)
}

const fetchScoutMockJson = async (fileName, callback) => {
  try {
    const res = await fetch(`${SCOUT_MOCK_BASE}/${fileName}`)
    if (!res || !res.ok) {
      safeCallback(callback, false)
      return
    }
    const data = await res.json()
    safeCallback(callback, data)
  } catch (e) {
    apiDebug('Scout mock fetch error', e)
    safeCallback(callback, false)
  }
}

const parseMockInferenceTarget = (data = {}) => {
  const rawTankId = String(data?.tankId || '').trim()
  const rawRef = String(data?.ref || '').trim()
  const fromRef = (ref, expectedScope) => {
    const value = String(ref || '')
    if (!value) return null
    const parts = value.split('#').filter(Boolean)
    if (parts.length >= 3) {
      const scope = String(parts[parts.length - 1] || '').toLowerCase()
      if (scope === expectedScope) {
        return { tankId: parts[parts.length - 2], scope }
      }
    }
    return null
  }

  if (rawTankId.includes('#')) {
    const parts = rawTankId.split('#').filter(Boolean)
    if (parts.length >= 2) {
      return { tankId: parts[0], scope: 'batch' }
    }
  }

  const fromGenericRef = fromRef(rawRef, 'batch')
  if (fromGenericRef) return fromGenericRef

  const fromBatchRef = fromRef(data?.batchRef || data?.batchChartRef, 'batch')
  if (fromBatchRef) return fromBatchRef

  // Fallback to the selected tank asset id.
  return { tankId: rawTankId, scope: 'batch' }
}

const normalizeInferenceQueryForScoutV2 = (data = {}, { scoutV2Enabled, triageBoard }) => {
  const input = data && typeof data === 'object' ? { ...data } : {}
  const hasScopedRef = Boolean(input.ref || input.batchRef || input.batchChartRef)
  if (!scoutV2Enabled || hasScopedRef) return input

  const tankId = String(input.tankId || '').trim()
  if (!tankId) return input

  const refsByTankScope = triageBoard?.board?.charts?.chartRefsByTankScope || {}
  const refs = refsByTankScope?.[tankId] || {}
  const scopedRef = String(refs?.batch || '').trim()
  if (!scopedRef) return input

  return { ref: scopedRef }
}

export default function useApi() {
  const dispatch = useDispatch()
  const { config, site, sites, assets, user, energy, power, siteLogs, templates, yeasts, apiKeys, plaatoDevices, amberSites,
    amberPrices, amberOverrides, triageBoard, assistantEfficiencyForecast, scoutActionHistory, scoutContext,
    erpProductTypes, erpProductType, erpProcessFlows, erpProcessFlow,
    erpIngredients, erpIngredient, erpSuppliers, erpSupplier,
    erpRecipes, erpRecipe, erpInventoryItems, erpInventoryTransactions, erpInventoryLots,
    erpBatches, erpBatch, erpBatchLogs, erpBatchLabs, erpAssets,
    erpPackagingItems, erpPackagingItem, erpPackagingRuns, erpPackagingRun,
    erpOtherItems, erpOtherItem,
    erpSkus, erpSku, erpExciseConfig, erpExciseRates, erpAmr,
    erpExciseReturns, erpExciseReturn, erpDispatches, erpExciseRecords, erpRecallTrace,
    erpCustomers, erpCustomer, erpOrders, erpOrder,
    erpBarrels,
    erpBarrel,
    erpBarrelEvents,
    erpPickingRuns,
    erpPickingRun,
    erpShipments,
    erpShipment,
    erpPurchaseOrders,
    erpPurchaseOrder,
    erpResources,
    erpResource,
    erpResourceEvents,
    erpResourceEvent,
    erpTrashEvents,
    erpXeroStatus,
    erpXeroAuthUrl,
    erpXeroConfig,
    erpSquareStatus,
    erpSquareAuthUrl,
    erpSquareConfig,
    erpGotabStatus,
    erpGotabConfig,
    erpKountaStatus,
    erpKountaAuthUrl,
    erpKountaConfig } = useSelector((state) => state.api)

  return {
    config,
    site,
    sites,
    assets,
    energy,
    power,
    siteLogs,
    templates,
    yeasts,
    user,
    apiKeys,
    plaatoDevices,
    amberSites,
    amberOverrides,
    amberPrices,
    triageBoard,
    assistantEfficiencyForecast,
    scoutActionHistory,
    scoutContext,
    getConfig: (callback) => {
      dispatch(getData('application/config', null, false)).then(callback)
    },
    getConfigHistory: (callback) => {
      dispatch(getData('site/config/history', null, true)).then(callback)
    },
    getUser: (callback) => {
      dispatch(getData('user/get', null, true)).then(callback)
    },
    setUserAttribute: ({ username, attributeKey, attributeData }, callback) => {
      dispatch(
        postData(
          'admin/setattribute',
          {
            email: username,
            key: attributeKey,
            value: attributeData,
          },
          true
        )
      ).then(callback)
    },
    invokeCrudMethod: ({ method, data }, callback) => {
      dispatch(
        postData(
          method,
          data,
          true
        )
      ).then(callback)
    },
    getSitesOverview: (callback) => {
      dispatch(getData('admin/overview', null, true)).then(callback)
    },
    getApiAudit: (data, callback) => {
      const request = fetchData('admin/api-audit', data, true)
      if (typeof callback === 'function') request.then(callback)
      return request
    },
    getSite: (callback) => {
      dispatch(getData('site/get', null, true)).then(callback)
    },
    setSiteMap: (data, callback) => {
      dispatch(postData('site/sitemap/set', data, true)).then(callback)
    },
    setVisualLayout: (data, callback) => {
      dispatch(postData('site/visual-layout/set', data, true)).then(callback)
    },
    getSiteData: (callback) => {
      dispatch(getData('site/get/data', null, true)).then(callback)
    },
    listAssets: (callback) => {
      dispatch(getData('asset/list', null, true)).then(callback)
    },
    switchOutput: (data, callback) => {
      dispatch(postData('asset/output/switch', data, true)).then(callback)
    },
    setInputTarget: (data, callback) => {
      dispatch(postData('asset/input/target/set', data, true)).then(callback)
    },
    updateAsset: (data, callback) => {
      dispatch(postData('asset/update', data, true)).then(callback)
    },
    updateAssetOrder: (data, callback) => {
      dispatch(postData('asset/update', data, true)).then(callback)
    },
    setAssetConfig: (data, callback) => {
      dispatch(postData('asset/config/set', data, true)).then(callback)
    },
    updateSiteConfig: (data, callback) => {
      dispatch(postData('site/config/set', data, true)).then(callback)
    },
    updateSiteLockout: (data, callback) => {
      dispatch(postData('site/lockout/set', data, true)).then(callback)
    },
    updateAssistantConfig: (data, callback) => {
      dispatch(postData('site/assistant/config', data || {}, true)).then(callback)
    },
    updateDigestNotifications: (data, callback) => {
      dispatch(postData('site/notifications/set', data, true)).then(callback)
    },
    overrideSecondarySwitch: (data, callback) => {
      dispatch(postData('asset/secondary/set', data, true)).then(callback)
    },
    activateSite: (data, callback) => {
      dispatch(postData('site/activate', data, true)).then(callback)
    },
    listInputLogs: (data, callback) => {
      dispatch(getData('asset/input/log/list', data, true)).then(callback)
    },
    listOutputLogs: (data, callback) => {
      dispatch(getData('asset/output/log/list', data, true)).then(callback)
    },
    updatePriority: (data, callback) => {
      dispatch(postData('asset/priority/update', data, true)).then(callback)
    },
    updateSuperPriority: (data, callback) => {
      dispatch(postData('asset/superpriority/update', data, true)).then(callback)
    },
    updateHfMode: (data, callback) => {
      dispatch(postData('asset/hfmode/update', data, true)).then(callback)
    },
    updateKnockoutMode: (data, callback) => {
      dispatch(postData('asset/knockoutmode/update', data, true)).then(callback)
    },
    updateOverchill: (data, callback) => {
      dispatch(postData('asset/overchill/update', data, true)).then(callback)
    },
    updateAltSwitch: (data, callback) => {
      dispatch(postData('asset/altswitch/update', data, true)).then(callback)
    },
    getEnergyLogs: (data, callback) => {
      dispatch(getData('site/energy/log/list', data, true)).then(callback)
    },
    getPowerLogs: (data, callback) => {
      dispatch(getData('site/power/log/list', data, true)).then(callback)
    },
    getSiteLogs: (callback) => {
      dispatch(getData('site/logs/get', null, true)).then(callback)
    },
    retrieveSiteLogs: (callback) => {
      dispatch(postData('site/logs/retrieve', null, true)).then(callback)
    },
    createTemplate: (data, callback) => {
      dispatch(postData('template/create', data, true)).then(callback)
    },
    updateTemplate: (data, callback) => {
      dispatch(postData('template/update', data, true)).then(callback)
    },
    listTemplates: (callback) => {
      dispatch(getData('template/list', null, true)).then(callback)
    },
    listCurveTemplates: (callback) => {
      fetchData('curve-template/list', null, true).then(callback)
    },
    createCurveTemplate: (data, callback) => {
      dispatch(postData('curve-template/create', data, true)).then(callback)
    },
    updateCurveTemplate: (data, callback) => {
      dispatch(postData('curve-template/update', data, true)).then(callback)
    },
    duplicateCurveTemplate: (data, callback) => {
      dispatch(postData('curve-template/duplicate', data, true)).then(callback)
    },
    archiveCurveTemplate: (data, callback) => {
      dispatch(postData('curve-template/archive', data, true)).then(callback)
    },
    createBrew: (data, callback) => {
      dispatch(postData('brew/create', data, true)).then(callback)
    },
    updateBrew: (data, callback) => {
      dispatch(postData('brew/update', data, true)).then(callback)
    },
    updateBrewTemplate: (data, callback) => {
      dispatch(postData('brew/template/update', data, true)).then(callback)
    },
    finishBrew: (data, callback) => {
      dispatch(postData('brew/finish', data, true)).then(callback)
    },
    listBrews: (data, callback) => {
      dispatch(getData('brew/list', data, true)).then(callback)
    },
    listBrewLogs: (data, callback) => {
      dispatch(getData('brew/log/list', data, true)).then(callback)
    },
    listWastewaterLogs: (callback) => {
      dispatch(getData('wastewater/list', null, true)).then(callback)
    },
    wastewaterControl: (data, callback) => {
      dispatch(postData('wastewater/control', data, true)).then(callback)
    },
    getSiteStatus: (callback) => {
      dispatch(getData('status/get', null, true)).then(callback)
    },
    getSiteStatusLogs: (callback) => {
      dispatch(getData('/status/logs/get', null, true)).then(callback)
    },
    toggleOutput: (data, callback) => {
      dispatch(postData('status/output/toggle', data, true)).then(callback)
    },
    createCheckoutSession: (callback) => {
      dispatch(getData('payment/checkout', null, true)).then(callback)
    },
    retrieveSubscription: (callback) => {
      dispatch(getData('subscription', null, true)).then(callback)
    },
    testOutputs: (callback) => {
      dispatch(postData('test/outputs', null, true)).then(callback)
    },
    refresh: (callback) => {
      dispatch(postData('refresh', null, true)).then(callback)
    },
    signContract: (data, callback) => {
      dispatch(postData('site/contract/sign', data, true)).then(callback)
    },
    listNotes: (data, callback) => {
      dispatch(getData('note/list', data, true)).then(callback)
    },
    createNote: (data, callback) => {
      dispatch(postData('note/create', data, true)).then(callback)
    },
    updateNote: (data, callback) => {
      dispatch(postData('note/update', data, true)).then(callback)
    },
    transferBatch: (data, callback) => {
      dispatch(postData('brew/transfer', data, true)).then(callback)
    },
    deleteBatch: (data, callback) => {
      dispatch(postData('brew/delete', data, true)).then(callback)
    },
    getBatchReport: (data, callback) => {
      dispatch(getData('reports/batch', data, true)).then(callback)
    },
    exportAssetHistory: (data, callback) => {
      dispatch(getData('asset/history/export', data, true)).then(callback)
    },
    exportInputLogs: (data, callback) => {
      fetchData('asset/input/log/export', data, true).then(callback)
    },
    listChangelog: (callback) => {
      fetchData('changelog/list', null, true).then(callback)
    },
    listApiKeys: (callback) => {
      dispatch(getData('api/key/list', null, true)).then(callback)
    },
    createApiKey: (data, callback) => {
      dispatch(postData('api/key/create', data, true)).then(callback)
    },
    deleteApiKey: (data, callback) => {
      dispatch(postData('api/key/delete', data, true)).then(callback)
    },
    associateApiKey: (data, callback) => {
      dispatch(postData('integrate/associate', data, true)).then(callback)
    },
    listPlaatoDevices: (callback) => {
      dispatch(getData('integrate/plaato/device/list', null, true)).then(callback)
    },
    assignPlaatoDevice: (data, callback) => {
      dispatch(postData('integrate/plaato/device/assign', data, true)).then(callback)
    },
    inviteUser: (data, callback) => {
      dispatch(postData('user/invite', data, true)).then(callback)
    },
    deleteUser: (data, callback) => {
      dispatch(postData('user/delete', data, true)).then(callback)
    },
    updateUser: (data, callback) => {
      dispatch(postData('user/update', data, true)).then(callback)
    },
    listAmberSites: (callback) => {
      dispatch(getData('integrate/amber/sites/list', null, true)).then(callback)
    },
    setAmberConfig: (data, callback) => {
      dispatch(postData('integrate/amber/config', data, true)).then(callback)
    },
    getAmberPrices: (callback) => {
      dispatch(getData('integrate/amber/prices', null, true)).then(callback)
    },
    setPrimaryInput: (data, callback) => {
      dispatch(postData('asset/input/primary', data, true)).then(callback)
    },
    setProactive: (data, callback) => {
      dispatch(postData('site/proactive/update', data, true)).then(callback)
    },
    calibrateDensity: (data, callback) => {
      dispatch(postData('asset/density/calibrate', data, true)).then(callback)
    },
    setVintraceConfig: (data, callback) => {
      dispatch(postData('integrate/vintrace/config', data, true)).then(callback)
    },
    setBrewwConfig: (data, callback) => {
      dispatch(postData('integrate/breww/config', data, true)).then(callback)
    },
    setGoogleSheetsConfig: (data, callback) => {
      dispatch(postData('integrate/google/sheets/config', data, true)).then(callback)
    },
    getGoogleSheet: (data, callback) => {
      dispatch(postData('integrate/google/sheets/get', data, true)).then(callback)
    },
    setDynamicGlycolSetpoint: (data, callback) => {
      dispatch(postData('asset/dynamicglycol/update', data, true)).then(callback)
    },
    updateBrewLog: (data, callback) => {
      dispatch(postData('brew/log/update', data, true)).then(callback)
    },
    gitInstall: (callback) => {
      dispatch(postData('flow/git/install', 'post', true)).then(callback)
    },
    gitUpdate: (callback) => {
      dispatch(postData('flow/git/update', 'post', true)).then(callback)
    },
    refreshMsJwt: (callback) => {
      dispatch(postData('milesight/token/refresh', 'post', true)).then(callback)
    },
    setSiteTags: (data, callback) => {
      dispatch(postData('site/tags/set', data, true)).then(callback)
    },
    listYeasts: (data, callback) => {
      dispatch(getData('site/yeast/list', data, true)).then(callback)
    },
    //Local stuff
    scanForNearbyNetworks: (data, callback) => {
      dispatch(localRequest('scan', "post", data)).then(callback)
    },
    getInterfaces: (data, callback) => {
      dispatch(localRequest('interfaces', 'post', data)).then(callback)
    },
    removeNetwork: (data, callback) => {
      dispatch(localRequest('removeNetwork', 'post', data)).then(callback)
    },
    joinNetwork: (data, callback) => {
      dispatch(localRequest('joinNetwork', 'post', data)).then(callback)
    },
    getTriageBoard: (dataOrCallback, maybeCallback) => {
      const data = typeof dataOrCallback === 'function' ? null : dataOrCallback
      const callback = typeof dataOrCallback === 'function' ? dataOrCallback : maybeCallback
      if (SCOUT_MOCK_MODE) {
        fetchScoutMockJson('triageboard.get.json', callback)
        return
      }
      dispatch(getData('assistant/triageboard/get', data, true)).then(callback)
    },
    getAssetScoutInsight: (data, callback) => {
      dispatch(getData('assistant/asset/insight', data, true)).then(callback)
    },
    getInferenceChart: (data, callback) => {
      const scoutV2Enabled = Boolean(site?.assistantConfig?.scoutV2Enabled)
      const normalizedData = normalizeInferenceQueryForScoutV2(data, {
        scoutV2Enabled,
        triageBoard
      })
      if (SCOUT_MOCK_MODE) {
        const target = parseMockInferenceTarget(normalizedData)
        const tankId = String(target?.tankId || '').trim()
        const scope = String(target?.scope || 'batch').toLowerCase() === 'tank' ? 'tank' : 'batch'
        if (!tankId) {
          safeCallback(callback, false)
          return
        }
        fetchScoutMockJson(`inferencechart.get.${tankId}.${scope}.json`, callback)
        return
      }
      dispatch(getData('assistant/inferencechart/get', normalizedData, true)).then(callback)
    },
    refreshScout: (data, callback) => {
      dispatch(postData('assistant/scout/refresh', data || {}, true)).then(callback)
    },
    getScoutRefreshQuota: (data, callback) => {
      dispatch(getData('assistant/scout/refresh/quota', data || {}, true)).then(callback)
    },
    refreshTankScout: (data, callback) => {
      dispatch(postData('assistant/scout/tank/refresh', data || {}, true)).then(callback)
    },
    executeScoutAction: (data, callback) => {
      dispatch(postData('assistant/actions/execute', data || {}, true)).then(callback)
    },
    getScoutActionHistory: (data, callback) => {
      dispatch(getData('assistant/actions/history', data || {}, true)).then(callback)
    },
    getScoutContext: (data, callback) => {
      dispatch(getData('assistant/scout/context', data || {}, true)).then(callback)
    },
    sendScoutChat: (data, callback) => {
      dispatch(postData('assistant/chat', data || {}, true)).then(callback)
    },
    getAssistantEfficiencyForecast: (dataOrCallback, maybeCallback) => {
      const data = typeof dataOrCallback === 'function' ? null : dataOrCallback
      const callback = typeof dataOrCallback === 'function' ? dataOrCallback : maybeCallback
      if (SCOUT_MOCK_MODE) {
        fetchScoutMockJson('efficiency.forecast.get.json', callback)
        return
      }
      dispatch(getData('assistant/efficiency/forecast/get', data, true)).then(callback)
    },
    refreshAssistantEfficiencyForecast: (data, callback) => {
      dispatch(postData('assistant/efficiency/forecast', data || {}, true)).then(callback)
    },
    trainAssistantEfficiency: (data, callback) => {
      dispatch(postData('assistant/efficiency/train', data || {}, true)).then(callback)
    },
    bootstrapAssistantModels: (data, callback) => {
      dispatch(postData('assistant/models/bootstrap', data || {}, true)).then(callback)
    },
    setAssistantModelTraining: (data, callback) => {
      dispatch(postData('assistant/models/training/set', data || {}, true)).then(callback)
    },
    retrainAssistantModels: (data, callback) => {
      dispatch(postData('assistant/models/retrain', data || {}, true)).then(callback)
    },
    clearAssistantModel: (data, callback) => {
      dispatch(postData('assistant/models/batch/clear', data || {}, true)).then(callback)
    },
    switchPressureValve: (data, callback) => {
      dispatch(postData('asset/pressure/override', data, true)).then(callback)
    },
    overrideAsset: (data, callback) => {
      dispatch(postData('asset/override', data, true)).then(callback)
    },
    acknowledgeVersion: (data, callback) => {
      const email = user?.email;
      if (!email) return;
      // Update local Redux state immediately so the banner hides
      dispatch({ type: 'api/site/version/acknowledge', payload: { data: { version: data.version, email } } });
      // Persist the acknowledgement on the site object
      dispatch(postData('site/version/acknowledge', { version: data.version, email }, true)).then(callback);
    },
    getEnergyMeters: (callback) => {
      dispatch(getData('site/energy/meters', null, true)).then(callback)
    },
    setEnergyMetersConfig: (data, callback) => {
      dispatch(postData('site/energy/meters/config', data, true)).then(callback)
    },
    getMerossHistory: (data, callback) => {
      dispatch(getData('site/meross/history/list', data, true)).then(callback)
    },

    // ------------------------------------------------------------------
    // ERP
    // ------------------------------------------------------------------
    erpProductTypes,
    erpProductType,
    erpProcessFlows,
    erpProcessFlow,
    erpIngredients,
    erpIngredient,
    erpSuppliers,
    erpSupplier,
    erpRecipes,
    erpRecipe,
    erpInventoryItems,
    erpInventoryTransactions,
    erpInventoryLots,
    erpBatches,
    erpBatch,
    erpBatchLogs,
    erpBatchLabs,
erpAssets,
    erpResources,
    erpResource,
    erpResourceEvents,
    erpResourceEvent,
    erpPackagingItems,
    erpPackagingItem,
    erpPackagingRuns,
    erpPackagingRun,
    erpOtherItems,
    erpOtherItem,
    erpSkus,
    erpSku,
    erpExciseConfig,
    erpExciseRates,
    erpAmr,
    erpExciseReturns,
    erpExciseReturn,
    erpDispatches,
    erpExciseRecords,
    erpRecallTrace,

    // Product types
    erpListProductTypes: (data, callback) => {
      dispatch(getData('erp/product-type/list', data, true)).then(callback)
    },
    erpGetProductType: (data, callback) => {
      dispatch(getData('erp/product-type/get', data, true)).then(callback)
    },
    erpCreateProductType: (data, callback) => {
      dispatch(postData('erp/product-type/create', data, true)).then(callback)
    },
    erpUpdateProductType: (data, callback) => {
      dispatch(postData('erp/product-type/update', data, true)).then(callback)
    },
    erpDeleteProductType: (data, callback) => {
      dispatch(postData('erp/product-type/delete', data, true)).then(callback)
    },

    // Process flows
    erpListProcessFlows: (data, callback) => {
      dispatch(getData('erp/process-flow/list', data, true)).then(callback)
    },
    erpGetProcessFlow: (data, callback) => {
      dispatch(getData('erp/process-flow/get', data, true)).then(callback)
    },
    erpCreateProcessFlow: (data, callback) => {
      dispatch(postData('erp/process-flow/create', data, true)).then(callback)
    },
    erpUpdateProcessFlow: (data, callback) => {
      dispatch(postData('erp/process-flow/update', data, true)).then(callback)
    },
    erpDeleteProcessFlow: (data, callback) => {
      dispatch(postData('erp/process-flow/delete', data, true)).then(callback)
    },

    // Ingredients
    erpListIngredients: (data, callback) => {
      dispatch(getData('erp/ingredient/list', data, true)).then(callback)
    },
    erpGetIngredient: (data, callback) => {
      dispatch(getData('erp/ingredient/get', data, true)).then(callback)
    },
    erpCreateIngredient: (data, callback) => {
      dispatch(postData('erp/ingredient/create', data, true)).then(callback)
    },
    erpUpdateIngredient: (data, callback) => {
      dispatch(postData('erp/ingredient/update', data, true)).then(callback)
    },
    erpDeleteIngredient: (data, callback) => {
      dispatch(postData('erp/ingredient/delete', data, true)).then(callback)
    },

    // Suppliers
    erpListSuppliers: (data, callback) => {
      dispatch(getData('erp/supplier/list', data, true)).then(callback)
    },
    erpGetSupplier: (data, callback) => {
      dispatch(getData('erp/supplier/get', data, true)).then(callback)
    },
    erpCreateSupplier: (data, callback) => {
      dispatch(postData('erp/supplier/create', data, true)).then(callback)
    },
    erpUpdateSupplier: (data, callback) => {
      dispatch(postData('erp/supplier/update', data, true)).then(callback)
    },
    erpDeleteSupplier: (data, callback) => {
      dispatch(postData('erp/supplier/delete', data, true)).then(callback)
    },

    // Recipes
    erpListRecipes: (data, callback) => {
      dispatch(getData('erp/recipe/list', data, true)).then(callback)
    },
    erpGetRecipe: (data, callback) => {
      dispatch(getData('erp/recipe/get', data, true)).then(callback)
    },
    erpCreateRecipe: (data, callback) => {
      dispatch(postData('erp/recipe/create', data, true)).then(callback)
    },
    erpUpdateRecipe: (data, callback) => {
      dispatch(postData('erp/recipe/update', data, true)).then(callback)
    },
    erpDeleteRecipe: (data, callback) => {
      dispatch(postData('erp/recipe/delete', data, true)).then(callback)
    },

    // Inventory
    erpListInventory: (data, callback) => {
      dispatch(getData('erp/inventory/list', data, true)).then(callback)
    },
    erpReceiveInventory: (data, callback) => {
      dispatch(postData('erp/inventory/receive', data, true)).then(callback)
    },
    erpAdjustInventory: (data, callback) => {
      dispatch(postData('erp/inventory/adjust', data, true)).then(callback)
    },
    erpListInventoryTransactions: (data, callback) => {
      dispatch(getData('erp/inventory/transactions', data, true)).then(callback)
    },
    erpListInventoryLots: (data, callback) => {
      dispatch(getData('erp/inventory/lots', data, true)).then(callback)
    },
    erpDeleteInventoryItem: (data, callback) => {
      dispatch(postData('erp/inventory/delete', data, true)).then(callback)
    },

    // Batches
    erpListBatches: (data, callback) => {
      dispatch(getData('erp/batch/list', data, true)).then(callback)
    },
    erpGetBatch: (data, callback) => {
      dispatch(getData('erp/batch/get', data, true)).then(callback)
    },
    erpCreateBatch: (data, callback) => {
      dispatch(postData('erp/batch/create', data, true)).then(callback)
    },
    erpUpdateBatch: (data, callback) => {
      dispatch(postData('erp/batch/update', data, true)).then(callback)
    },
    erpDeleteBatch: (data, callback) => {
      dispatch(postData('erp/batch/delete', data, true)).then(callback)
    },
    erpChangeBatchStatus: (data, callback) => {
      dispatch(postData('erp/batch/status', data, true)).then(callback)
    },
    erpSignOffGate: (data, callback) => {
      dispatch(postData('erp/batch/signoff-gate', data, true)).then(callback)
    },
    erpAddBatchLog: (data, callback) => {
      dispatch(postData('erp/batch/log', data, true)).then(callback)
    },
    erpListBatchLogs: (data, callback) => {
      dispatch(getData('erp/batch/logs', data, true)).then(callback)
    },
    erpAddBatchLab: (data, callback) => {
      dispatch(postData('erp/batch/lab', data, true)).then(callback)
    },
    erpListBatchLabs: (data, callback) => {
      dispatch(getData('erp/batch/labs', data, true)).then(callback)
    },

    // Packaging items
    erpListPackagingItems: (data, callback) => {
      dispatch(getData('erp/packaging-item/list', data, true)).then(callback)
    },
    erpGetPackagingItem: (data, callback) => {
      dispatch(getData('erp/packaging-item/get', data, true)).then(callback)
    },
    erpCreatePackagingItem: (data, callback) => {
      dispatch(postData('erp/packaging-item/create', data, true)).then(callback)
    },
    erpUpdatePackagingItem: (data, callback) => {
      dispatch(postData('erp/packaging-item/update', data, true)).then(callback)
    },
    erpDeletePackagingItem: (data, callback) => {
      dispatch(postData('erp/packaging-item/delete', data, true)).then(callback)
    },

    // Packaging runs
    erpListPackagingRuns: (data, callback) => {
      dispatch(getData('erp/packaging-run/list', data, true)).then(callback)
    },
    erpGetPackagingRun: (data, callback) => {
      dispatch(getData('erp/packaging-run/get', data, true)).then(callback)
    },
    erpCreatePackagingRun: (data, callback) => {
      dispatch(postData('erp/packaging-run/create', data, true)).then(callback)
    },
    erpUpdatePackagingRun: (data, callback) => {
      dispatch(postData('erp/packaging-run/update', data, true)).then(callback)
    },
    erpDeletePackagingRun: (data, callback) => {
      dispatch(postData('erp/packaging-run/delete', data, true)).then(callback)
    },

    // Other inventory items
    erpListOtherItems: (data, callback) => {
      dispatch(getData('erp/other-item/list', data, true)).then(callback)
    },
    erpGetOtherItem: (data, callback) => {
      dispatch(getData('erp/other-item/get', data, true)).then(callback)
    },
    erpCreateOtherItem: (data, callback) => {
      dispatch(postData('erp/other-item/create', data, true)).then(callback)
    },
    erpUpdateOtherItem: (data, callback) => {
      dispatch(postData('erp/other-item/update', data, true)).then(callback)
    },
    erpDeleteOtherItem: (data, callback) => {
      dispatch(postData('erp/other-item/delete', data, true)).then(callback)
    },

    // SKUs / Finished Goods
    erpListSkus: (data, callback) => {
      dispatch(getData('erp/sku/list', data, true)).then(callback)
    },
    erpGetSku: (data, callback) => {
      dispatch(getData('erp/sku/get', data, true)).then(callback)
    },
    erpCreateSku: (data, callback) => {
      dispatch(postData('erp/sku/create', data, true)).then(callback)
    },
    erpUpdateSku: (data, callback) => {
      dispatch(postData('erp/sku/update', data, true)).then(callback)
    },
    erpDeleteSku: (data, callback) => {
      dispatch(postData('erp/sku/delete', data, true)).then(callback)
    },

    // Excise & Compliance
    erpGetExciseConfig: (data, callback) => {
      dispatch(getData('erp/excise/config', data, true)).then(callback)
    },
    erpSetExciseConfig: (data, callback) => {
      dispatch(postData('erp/excise/config', data, true)).then(callback)
    },
    erpListExciseRates: (data, callback) => {
      dispatch(getData('erp/excise/rates', data, true)).then(callback)
    },
    erpCreateExciseRate: (data, callback) => {
      dispatch(postData('erp/excise/rate', data, true)).then(callback)
    },
    erpGetAmr: (data, callback) => {
      dispatch(getData('erp/excise/amr', data, true)).then(callback)
    },
    erpAdjustAmr: (data, callback) => {
      dispatch(postData('erp/excise/amr', data, true)).then(callback)
    },
    erpCalculateExcise: (data, callback) => {
      dispatch(postData('erp/excise/calculate', data, true)).then(callback)
    },
    erpGenerateExciseReturn: (data, callback) => {
      dispatch(postData('erp/excise/return/generate', data, true)).then(callback)
    },
    erpListExciseReturns: (data, callback) => {
      dispatch(getData('erp/excise/return/list', data, true)).then(callback)
    },
    erpGetExciseReturn: (data, callback) => {
      dispatch(getData('erp/excise/return/get', data, true)).then(callback)
    },
    erpCreateDispatch: (data, callback) => {
      dispatch(postData('erp/excise/dispatch', data, true)).then(callback)
    },
    erpListDispatches: (data, callback) => {
      dispatch(getData('erp/excise/dispatch/list', data, true)).then(callback)
    },
    erpTraceRecall: (data, callback) => {
      dispatch(getData('erp/excise/recall', data, true)).then(callback)
    },
    erpListExciseAudit: (data, callback) => {
      dispatch(getData('erp/excise/audit', data, true)).then(callback)
    },

    // Customers
    erpCustomers,
    erpCustomer,
    erpListCustomers: (data, callback) => {
      dispatch(getData('erp/customer/list', data, true)).then(callback)
    },
    erpGetCustomer: (data, callback) => {
      dispatch(getData('erp/customer/get', data, true)).then(callback)
    },
    erpCreateCustomer: (data, callback) => {
      dispatch(postData('erp/customer/create', data, true)).then(callback)
    },
    erpUpdateCustomer: (data, callback) => {
      dispatch(postData('erp/customer/update', data, true)).then(callback)
    },
    erpDeleteCustomer: (data, callback) => {
      dispatch(postData('erp/customer/delete', data, true)).then(callback)
    },

    // Orders
    erpOrders,
    erpOrder,
    erpListOrders: (data, callback) => {
      dispatch(getData('erp/order/list', data, true)).then(callback)
    },
    erpGetOrder: (data, callback) => {
      dispatch(getData('erp/order/get', data, true)).then(callback)
    },
    erpCreateOrder: (data, callback) => {
      dispatch(postData('erp/order/create', data, true)).then(callback)
    },
    erpUpdateOrder: (data, callback) => {
      dispatch(postData('erp/order/update', data, true)).then(callback)
    },
    erpDeleteOrder: (data, callback) => {
      dispatch(postData('erp/order/delete', data, true)).then(callback)
    },
    erpFulfillOrderLine: (data, callback) => {
      dispatch(postData('erp/order/fulfill', data, true)).then(callback)
    },
    erpDeleteDispatch: (data, callback) => {
      dispatch(postData('erp/excise/dispatch/delete', data, true)).then(callback)
    },

    // Barrels
    erpBarrels,
    erpBarrel,
    erpBarrelEvents,
    erpListBarrels: (data, callback) => {
      dispatch(getData('erp/barrel/list', data, true)).then(callback)
    },
    erpGetBarrel: (data, callback) => {
      dispatch(getData('erp/barrel/get', data, true)).then(callback)
    },
    erpCreateBarrel: (data, callback) => {
      dispatch(postData('erp/barrel/create', data, true)).then(callback)
    },
    erpUpdateBarrel: (data, callback) => {
      dispatch(postData('erp/barrel/update', data, true)).then(callback)
    },
    erpDeleteBarrel: (data, callback) => {
      dispatch(postData('erp/barrel/delete', data, true)).then(callback)
    },
    erpFillBarrel: (data, callback) => {
      dispatch(postData('erp/barrel/fill', data, true)).then(callback)
    },
    erpTopBarrel: (data, callback) => {
      dispatch(postData('erp/barrel/top', data, true)).then(callback)
    },
    erpTransferBarrel: (data, callback) => {
      dispatch(postData('erp/barrel/transfer', data, true)).then(callback)
    },
    erpEmptyBarrel: (data, callback) => {
      dispatch(postData('erp/barrel/empty', data, true)).then(callback)
    },
    erpSampleBarrel: (data, callback) => {
      dispatch(postData('erp/barrel/sample', data, true)).then(callback)
    },
    erpListBarrelEvents: (data, callback) => {
      dispatch(getData('erp/barrel/events', data, true)).then(callback)
    },
    erpRecordBarrelEvent: (data, callback) => {
      dispatch(postData('erp/barrel/event', data, true)).then(callback)
    },

    // Picking
    erpPickingRuns,
    erpPickingRun,
    erpListPickingRuns: (data, callback) => {
      dispatch(getData('erp/picking/list', data, true)).then(callback)
    },
    erpGetPickingRun: (data, callback) => {
      dispatch(getData('erp/picking/get', data, true)).then(callback)
    },
    erpCreatePickingRun: (data, callback) => {
      dispatch(postData('erp/picking/create', data, true)).then(callback)
    },
    erpUpdatePickingRun: (data, callback) => {
      dispatch(postData('erp/picking/update', data, true)).then(callback)
    },
    erpDeletePickingRun: (data, callback) => {
      dispatch(postData('erp/picking/delete', data, true)).then(callback)
    },

    // Shipping
    erpShipments,
    erpShipment,
    erpListShipments: (data, callback) => {
      dispatch(getData('erp/shipping/list', data, true)).then(callback)
    },
    erpGetShipment: (data, callback) => {
      dispatch(getData('erp/shipping/get', data, true)).then(callback)
    },
    erpCreateShipment: (data, callback) => {
      dispatch(postData('erp/shipping/create', data, true)).then(callback)
    },
    erpUpdateShipment: (data, callback) => {
      dispatch(postData('erp/shipping/update', data, true)).then(callback)
    },
    erpDeleteShipment: (data, callback) => {
      dispatch(postData('erp/shipping/delete', data, true)).then(callback)
    },
    erpMarkShipmentDelivered: (data, callback) => {
      dispatch(postData('erp/shipping/mark-delivered', data, true)).then(callback)
    },

    // Purchase orders
    erpPurchaseOrders,
    erpPurchaseOrder,
    erpListPurchaseOrders: (data, callback) => {
      dispatch(getData('erp/po/list', data, true)).then(callback)
    },
    erpGetPurchaseOrder: (data, callback) => {
      dispatch(getData('erp/po/get', data, true)).then(callback)
    },
    erpCreatePurchaseOrder: (data, callback) => {
      dispatch(postData('erp/po/create', data, true)).then(callback)
    },
    erpUpdatePurchaseOrder: (data, callback) => {
      dispatch(postData('erp/po/update', data, true)).then(callback)
    },
    erpDeletePurchaseOrder: (data, callback) => {
      dispatch(postData('erp/po/delete', data, true)).then(callback)
    },
    erpReceivePurchaseOrder: (data, callback) => {
      dispatch(postData('erp/po/receive', data, true)).then(callback)
    },

    // Scheduling resources
    erpResources,
    erpResource,
    erpListResources: (data, callback) => {
      dispatch(getData('erp/resource/list', data, true)).then(callback)
    },
    erpGetResource: (data, callback) => {
      dispatch(getData('erp/resource/get', data, true)).then(callback)
    },
    erpSaveResource: (data, callback) => {
      dispatch(postData('erp/resource/save', data, true)).then(callback)
    },
    erpDeleteResource: (data, callback) => {
      dispatch(postData('erp/resource/delete', data, true)).then(callback)
    },
    erpListResourceEvents: (data, callback) => {
      dispatch(getData('erp/resource/event/list', data, true)).then(callback)
    },
    erpGetResourceEvent: (data, callback) => {
      dispatch(getData('erp/resource/event/get', data, true)).then(callback)
    },
    erpSaveResourceEvent: (data, callback) => {
      dispatch(postData('erp/resource/event/save', data, true)).then(callback)
    },
    erpDeleteResourceEvent: (data, callback) => {
      dispatch(postData('erp/resource/event/delete', data, true)).then(callback)
    },

    // Trash / Recycle Bin
    erpTrashEvents,
    erpListDeleteEvents: (data, callback) => {
      dispatch(getData('erp/trash/list', data, true)).then(callback)
    },
    erpRestoreDeleted: (data, callback) => {
      dispatch(postData('erp/trash/restore', data, true)).then(callback)
    },
    erpPurgeDeleted: (data, callback) => {
      dispatch(postData('erp/trash/purge', data, true)).then(callback)
    },

    // Xero integration
    erpXeroStatus,
    erpXeroAuthUrl,
    erpXeroConfig,
    erpGetXeroAuthUrl: (data, callback) => {
      dispatch(getData('erp/integration/xero/auth-url', data, true)).then(callback)
    },
    erpGetXeroStatus: (data, callback) => {
      dispatch(getData('erp/integration/xero/status', data, true)).then(callback)
    },
    erpXeroDisconnect: (data, callback) => {
      dispatch(postData('erp/integration/xero/disconnect', data, true)).then(callback)
    },
    erpXeroSyncInvoice: (data, callback) => {
      dispatch(postData('erp/integration/xero/sync-invoices', data, true)).then(callback)
    },
    erpXeroSyncPayments: (data, callback) => {
      dispatch(postData('erp/integration/xero/sync-payments', data, true)).then(callback)
    },
    erpGetXeroConfig: (data, callback) => {
      dispatch(getData('erp/integration/xero/config', data, true)).then(callback)
    },
    erpSaveXeroConfig: (data, callback) => {
      dispatch(postData('erp/integration/xero/config', data, true)).then(callback)
    },
    erpDeleteXeroConfig: (data, callback) => {
      dispatch(postData('erp/integration/xero/delete-config', data, true)).then(callback)
    },

    // Square POS integration
    erpSquareStatus,
    erpSquareAuthUrl,
    erpSquareConfig,
    erpGetSquareAuthUrl: (data, callback) => {
      dispatch(getData('erp/integration/square/auth-url', data, true)).then(callback)
    },
    erpGetSquareStatus: (data, callback) => {
      dispatch(getData('erp/integration/square/status', data, true)).then(callback)
    },
    erpSquareDisconnect: (data, callback) => {
      dispatch(postData('erp/integration/square/disconnect', data, true)).then(callback)
    },
    erpGetSquareConfig: (data, callback) => {
      dispatch(getData('erp/integration/square/config', data, true)).then(callback)
    },
    erpSaveSquareConfig: (data, callback) => {
      dispatch(postData('erp/integration/square/config', data, true)).then(callback)
    },
    erpDeleteSquareConfig: (data, callback) => {
      dispatch(postData('erp/integration/square/delete-config', data, true)).then(callback)
    },

    // GoTab POS integration
    erpGotabStatus,
    erpGotabConfig,
    erpGetGotabStatus: (data, callback) => {
      dispatch(getData('erp/integration/gotab/status', data, true)).then(callback)
    },
    erpGetGotabLocations: (data, callback) => {
      dispatch(getData('erp/integration/gotab/locations', data, true)).then(callback)
    },
    erpGotabConnect: (data, callback) => {
      dispatch(postData('erp/integration/gotab/connect', data, true)).then(callback)
    },
    erpGotabDisconnect: (data, callback) => {
      dispatch(postData('erp/integration/gotab/disconnect', data, true)).then(callback)
    },
    erpGotabReconcile: (data, callback) => {
      dispatch(postData('erp/integration/gotab/reconcile', data, true)).then(callback)
    },
    erpGetGotabConfig: (data, callback) => {
      dispatch(getData('erp/integration/gotab/config', data, true)).then(callback)
    },
    erpSaveGotabConfig: (data, callback) => {
      dispatch(postData('erp/integration/gotab/config', data, true)).then(callback)
    },
    erpDeleteGotabConfig: (data, callback) => {
      dispatch(postData('erp/integration/gotab/delete-config', data, true)).then(callback)
    },

    // Kounta POS integration
    erpKountaStatus,
    erpKountaAuthUrl,
    erpKountaConfig,
    erpGetKountaAuthUrl: (data, callback) => {
      dispatch(getData('erp/integration/kounta/auth-url', data, true)).then(callback)
    },
    erpGetKountaStatus: (data, callback) => {
      dispatch(getData('erp/integration/kounta/status', data, true)).then(callback)
    },
    erpGetKountaSites: (data, callback) => {
      dispatch(getData('erp/integration/kounta/sites', data, true)).then(callback)
    },
    erpKountaRegisterWebhook: (data, callback) => {
      dispatch(postData('erp/integration/kounta/register-webhook', data, true)).then(callback)
    },
    erpKountaDisconnect: (data, callback) => {
      dispatch(postData('erp/integration/kounta/disconnect', data, true)).then(callback)
    },
    erpKountaReconcile: (data, callback) => {
      dispatch(postData('erp/integration/kounta/reconcile', data, true)).then(callback)
    },
    erpGetKountaConfig: (data, callback) => {
      dispatch(getData('erp/integration/kounta/config', data, true)).then(callback)
    },
    erpSaveKountaConfig: (data, callback) => {
      dispatch(postData('erp/integration/kounta/config', data, true)).then(callback)
    },
    erpDeleteKountaConfig: (data, callback) => {
      dispatch(postData('erp/integration/kounta/delete-config', data, true)).then(callback)
    },

    // Assets (vessels / equipment). For sites with Frigid Cloud assets, use the
    // existing asset/list API. For standalone ERP sites, this is a stub that will
    // be wired to a dedicated ERP asset list endpoint later.
    erpListAssets: (data, callback) => {
      // TODO: implement standalone ERP asset list endpoint (erp/asset/list)
      // and dispatch(getData('erp/asset/list', data, true)).then(callback)
      if (callback) callback({ success: true, items: [] })
    }
  }
}
